import type { CRMUser, ProjectTask, TaskActivityLog, TaskPriority, TaskStatus } from '../types';
import { DEPARTMENT_DISPLAY_LABELS, type ProjectDepartment } from '../types';
import { getActionRequiredFullLabel, isActionRequiredExternal } from './actionRequiredDisplay';

const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'Not Started',
  'in-progress': 'In Progress',
  review: 'Review',
  completed: 'Done',
  blocked: 'Blocked',
  'on-hold': 'On Hold',
};

const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
};

function formatDate(d: Date | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function namesForUids(uids: string[], team: Pick<CRMUser, 'uid' | 'displayName'>[]): string {
  if (!uids.length) return 'None';
  return uids
    .map((uid) => team.find((u) => u.uid === uid)?.displayName || uid.slice(0, 8) + '…')
    .join(', ');
}

function edgesSignature(edges: ProjectTask['dependencyEdges']): string {
  if (!edges?.length) return '';
  return JSON.stringify(
    [...edges].sort((a, b) => a.predecessorId.localeCompare(b.predecessorId)),
  );
}

function arLabel(
  actionRequired: string | undefined,
  actionRequiredNote: string | undefined,
  team: Pick<CRMUser, 'uid' | 'displayName'>[],
): string {
  return getActionRequiredFullLabel(actionRequired, actionRequiredNote, team) || 'None';
}

/** Disambiguate audit lines when two different internal values map to the same display label. */
function summarizeArKey(task: Pick<ProjectTask, 'actionRequired' | 'actionRequiredNote'>): string {
  if (!task.actionRequired) return 'cleared';
  if (isActionRequiredExternal(task.actionRequired)) {
    const n = (task.actionRequiredNote || '').trim();
    return n ? `external note (${n.slice(0, 48)}${n.length > 48 ? '…' : ''})` : 'external';
  }
  return `uid ${task.actionRequired.slice(0, 10)}…`;
}

export type CreateTaskActivityFn = (log: Omit<TaskActivityLog, 'id' | 'createdAt'>) => Promise<string>;

export interface TaskActivityWriteExtras {
  projectId?: string;
  delayCause?: TaskActivityLog['delayCause'];
  moveId?: string;
}

export interface BuildTaskActivityOptions {
  before: ProjectTask;
  updates: Partial<ProjectTask>;
  taskId: string;
  actorId: string;
  actorName: string;
  teamMembers: Pick<CRMUser, 'uid' | 'displayName'>[];
  extras?: TaskActivityWriteExtras;
}

/**
 * Builds audit rows for changed fields. Caller writes each to Firestore via `createTaskActivity`.
 */
export function buildTaskActivityLogEntries(o: BuildTaskActivityOptions): Omit<TaskActivityLog, 'id' | 'createdAt'>[] {
  const { before, updates, taskId, actorId, actorName, teamMembers, extras } = o;
  const after = { ...before, ...updates } as ProjectTask;
  const rows: Omit<TaskActivityLog, 'id' | 'createdAt'>[] = [];

  const push = (
    field: string | undefined,
    action: string,
    oldValue?: string,
    newValue?: string,
    extra?: Pick<Omit<TaskActivityLog, 'id' | 'createdAt' | 'taskId' | 'actorId' | 'actorName' | 'action' | 'field' | 'oldValue' | 'newValue'>, 'oldValueKey' | 'newValueKey' | 'oldStartMs' | 'oldEndMs' | 'newStartMs' | 'newEndMs'>,
  ) => {
    const row: Omit<TaskActivityLog, 'id' | 'createdAt'> = {
      taskId,
      actorId,
      actorName,
      action,
      field,
      oldValue,
      newValue,
    };
    if (extras?.projectId) row.projectId = extras.projectId;
    if (extras?.delayCause) row.delayCause = extras.delayCause;
    if (extras?.moveId) row.moveId = extras.moveId;
    if (extra?.oldValueKey !== undefined) row.oldValueKey = extra.oldValueKey;
    if (extra?.newValueKey !== undefined) row.newValueKey = extra.newValueKey;
    if (extra?.oldStartMs !== undefined) row.oldStartMs = extra.oldStartMs;
    if (extra?.oldEndMs !== undefined) row.oldEndMs = extra.oldEndMs;
    if (extra?.newStartMs !== undefined) row.newStartMs = extra.newStartMs;
    if (extra?.newEndMs !== undefined) row.newEndMs = extra.newEndMs;
    rows.push(row);
  };

  if ('actionRequired' in updates) {
    const oldV = arLabel(before.actionRequired, before.actionRequiredNote, teamMembers);
    const newV = arLabel(after.actionRequired, after.actionRequiredNote, teamMembers);
    const rawArChanged =
      before.actionRequired !== after.actionRequired ||
      (before.actionRequiredNote || '').trim() !== (after.actionRequiredNote || '').trim();
    if (rawArChanged) {
      const oldKey = before.actionRequired || undefined;
      const newKey = after.actionRequired || undefined;
      if (oldV !== newV) {
        push('actionRequired', 'updated action required', oldV, newV, { oldValueKey: oldKey, newValueKey: newKey });
      } else {
        push(
          'actionRequired',
          'updated action required',
          `${oldV} (${summarizeArKey(before)})`,
          `${newV} (${summarizeArKey(after)})`,
          { oldValueKey: oldKey, newValueKey: newKey },
        );
      }
    }
  }

  if ('actionRequiredNote' in updates && !('actionRequired' in updates)) {
    const oldN = (before.actionRequiredNote || '').trim() || '—';
    const newN = (after.actionRequiredNote || '').trim() || '—';
    if (oldN !== newN) {
      push('actionRequiredNote', 'updated action required note', oldN, newN);
    }
  }

  if (updates.status !== undefined && before.status !== after.status) {
    push('status', 'changed status', STATUS_LABELS[before.status], STATUS_LABELS[after.status]);
  }

  if (updates.priority !== undefined && before.priority !== after.priority) {
    push('priority', 'changed priority', PRIORITY_LABELS[before.priority], PRIORITY_LABELS[after.priority]);
  }

  if (updates.title !== undefined && before.title !== after.title) {
    push('title', 'renamed task', before.title, after.title);
  }

  if (updates.department !== undefined && before.department !== after.department) {
    push(
      'department',
      'changed department',
      DEPARTMENT_DISPLAY_LABELS[before.department as ProjectDepartment],
      DEPARTMENT_DISPLAY_LABELS[after.department as ProjectDepartment],
    );
  }

  if (updates.progress !== undefined && before.progress !== after.progress) {
    push('progress', 'updated progress', `${before.progress}%`, `${after.progress}%`);
  }

  if (updates.assignedTo !== undefined) {
    const a = [...before.assignedTo].sort().join(',');
    const b = [...after.assignedTo].sort().join(',');
    if (a !== b) {
      push(
        'assignedTo',
        'updated assignees',
        namesForUids(before.assignedTo, teamMembers),
        namesForUids(after.assignedTo, teamMembers),
      );
    }
  }

  if (updates.startDate !== undefined) {
    const ot = before.startDate ? new Date(before.startDate).getTime() : NaN;
    const nt = after.startDate ? new Date(after.startDate).getTime() : NaN;
    if (ot !== nt) {
      push('startDate', 'changed start date', formatDate(before.startDate), formatDate(after.startDate), {
        oldStartMs: Number.isFinite(ot) ? ot : undefined,
        newStartMs: Number.isFinite(nt) ? nt : undefined,
        oldEndMs: before.endDate ? new Date(before.endDate).getTime() : undefined,
        newEndMs: after.endDate ? new Date(after.endDate).getTime() : undefined,
      });
    }
  }

  if (updates.endDate !== undefined) {
    const ot = before.endDate ? new Date(before.endDate).getTime() : NaN;
    const nt = after.endDate ? new Date(after.endDate).getTime() : NaN;
    if (ot !== nt) {
      push('endDate', 'changed due date', formatDate(before.endDate), formatDate(after.endDate), {
        oldEndMs: Number.isFinite(ot) ? ot : undefined,
        newEndMs: Number.isFinite(nt) ? nt : undefined,
        oldStartMs: before.startDate ? new Date(before.startDate).getTime() : undefined,
        newStartMs: after.startDate ? new Date(after.startDate).getTime() : undefined,
      });
    }
  }

  if (updates.dependencyEdges !== undefined) {
    const os = edgesSignature(before.dependencyEdges);
    const ns = edgesSignature(after.dependencyEdges);
    if (os !== ns) {
      push('dependencyEdges', 'updated dependencies', os ? '…' : 'None', ns ? '…' : 'None');
    }
  }

  if (updates.groupId !== undefined && before.groupId !== after.groupId) {
    push('groupId', 'moved task group', before.groupId || '—', after.groupId || '—');
  }

  return rows;
}

/**
 * Fire-and-forget writes for each log entry.
 */
export function recordTaskActivityChanges(
  opts: BuildTaskActivityOptions & { createTaskActivity: CreateTaskActivityFn },
): void {
  const entries = buildTaskActivityLogEntries(opts);
  for (const entry of entries) {
    void opts.createTaskActivity(entry).catch((err) => {
      console.error('[taskActivityLogger] Failed to write activity log:', err);
    });
  }
}

export function isActionRequiredActivityField(field: string | undefined): boolean {
  return field === 'actionRequired' || field === 'actionRequiredNote';
}

export function buildScheduleMoveActivityEntry(opts: {
  taskId: string;
  projectId?: string;
  actorId: string;
  actorName: string;
  before: Pick<ProjectTask, 'startDate' | 'endDate'>;
  start: Date;
  end: Date;
}): Omit<TaskActivityLog, 'id' | 'createdAt'> {
  const oldStartMs = new Date(opts.before.startDate).getTime();
  const oldEndMs = new Date(opts.before.endDate).getTime();
  const newStartMs = opts.start.getTime();
  const newEndMs = opts.end.getTime();
  const row: Omit<TaskActivityLog, 'id' | 'createdAt'> = {
    taskId: opts.taskId,
    actorId: opts.actorId,
    actorName: opts.actorName,
    action: 'moved schedule',
    field: 'schedule',
    oldValue: `${formatDate(opts.before.startDate)} — ${formatDate(opts.before.endDate)}`,
    newValue: `${formatDate(opts.start)} — ${formatDate(opts.end)}`,
    oldStartMs,
    oldEndMs,
    newStartMs,
    newEndMs,
  };
  if (opts.projectId) row.projectId = opts.projectId;
  return row;
}
